import { useState, useEffect } from 'react';
import { submitLead } from '../../lib/api';

export default function LeadPopup() {
  const [visible, setVisible] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [status, setStatus] = useState('idle'); // idle | loading | success | error

  useEffect(() => {
    // Show after 3 seconds, only once per session
    if (sessionStorage.getItem('lead_popup_seen')) return;
    const timer = setTimeout(() => setVisible(true), 3000);
    return () => clearTimeout(timer);
  }, []);

  function dismiss() {
    sessionStorage.setItem('lead_popup_seen', '1');
    setVisible(false);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name || !form.email) return;
    setStatus('loading');
    try {
      await submitLead(form);
      setStatus('success');
      setTimeout(dismiss, 1800);
    } catch {
      setStatus('error');
    }
  }

  if (!visible) return null;

  const inp = {
    width: '100%', border: '1px solid #e5e7eb', padding: '11px 14px',
    fontSize: 13, outline: 'none', fontFamily: 'var(--font-body)',
    color: 'var(--dark)', background: '#fafafa', boxSizing: 'border-box',
    transition: 'border-color 0.2s',
  };

  return (
    <div
      onClick={dismiss}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 9999, padding: 20,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff', width: '100%', maxWidth: 440,
          boxShadow: '0 24px 80px rgba(0,0,0,0.3)',
          animation: 'popupIn 0.35s ease',
          overflow: 'hidden',
        }}
      >
        <style>{`
          @keyframes popupIn {
            from { opacity: 0; transform: translateY(24px); }
            to   { opacity: 1; transform: translateY(0); }
          }
        `}</style>

        {/* Header banner */}
        <div style={{
          background: 'var(--dark)', padding: '28px 28px 24px',
          textAlign: 'center', position: 'relative',
        }}>
          <button
            onClick={dismiss}
            style={{
              position: 'absolute', top: 12, right: 14,
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 22, color: 'rgba(255,255,255,0.45)', lineHeight: 1,
            }}
          >×</button>
          <div style={{
            fontSize: 10, letterSpacing: '0.28em', textTransform: 'uppercase',
            color: 'var(--gold)', fontWeight: 600, marginBottom: 10,
            fontFamily: 'var(--font-body)',
          }}>
            Exclusive Access
          </div>
          <h2 style={{
            fontFamily: 'var(--font-display)', fontSize: 'clamp(22px,5vw,30px)',
            fontWeight: 300, color: '#fff', letterSpacing: '-0.01em',
            margin: 0, lineHeight: 1.2,
          }}>
            Get Early Access to<br />New Arrivals
          </h2>
          <p style={{
            fontSize: 13, color: 'rgba(255,255,255,0.45)', marginTop: 10,
            lineHeight: 1.6, fontWeight: 300, fontFamily: 'var(--font-body)',
          }}>
            Be first to discover authenticated luxury pieces<br />before they go live.
          </p>
        </div>

        {/* Form */}
        <div style={{ padding: '24px 28px 28px' }}>
          {status === 'success' ? (
            <div style={{
              textAlign: 'center', padding: '20px 0',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
            }}>
              <div style={{
                width: 52, height: 52, borderRadius: '50%', background: '#dcfce7',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24,
              }}>✓</div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 300, color: 'var(--dark)' }}>
                You're on the list.
              </div>
              <div style={{ fontSize: 13, color: 'var(--grey-mid)' }}>
                We'll be in touch soon.
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <input
                placeholder="Full Name *"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                required
                style={inp}
                onFocus={e => e.target.style.borderColor = 'var(--dark)'}
                onBlur={e => e.target.style.borderColor = '#e5e7eb'}
              />
              <input
                type="email"
                placeholder="Email Address *"
                value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                required
                style={inp}
                onFocus={e => e.target.style.borderColor = 'var(--dark)'}
                onBlur={e => e.target.style.borderColor = '#e5e7eb'}
              />
              <input
                type="tel"
                placeholder="Phone Number"
                value={form.phone}
                onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                style={inp}
                onFocus={e => e.target.style.borderColor = 'var(--dark)'}
                onBlur={e => e.target.style.borderColor = '#e5e7eb'}
              />

              {status === 'error' && (
                <p style={{ fontSize: 11, color: '#dc2626', margin: 0 }}>
                  Something went wrong. Please try again.
                </p>
              )}

              <button
                type="submit"
                disabled={status === 'loading'}
                style={{
                  width: '100%', padding: '14px', background: 'var(--gold)',
                  color: 'var(--dark)', border: 'none', cursor: status === 'loading' ? 'wait' : 'pointer',
                  fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase',
                  fontWeight: 700, fontFamily: 'var(--font-body)',
                  opacity: status === 'loading' ? 0.7 : 1, transition: 'background 0.25s',
                  marginTop: 4,
                }}
                onMouseEnter={e => { if (status !== 'loading') e.currentTarget.style.background = '#b8992a'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'var(--gold)'; }}
              >
                {status === 'loading' ? 'Submitting…' : 'Get Early Access →'}
              </button>

              <button
                type="button"
                onClick={dismiss}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: 12, color: 'var(--grey-mid)', textAlign: 'center',
                  padding: '4px 0', fontFamily: 'var(--font-body)',
                  textDecoration: 'underline', textUnderlineOffset: 3,
                }}
              >
                No thanks, continue browsing
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}